import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Alert,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  School as SchoolIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const Capacitaciones = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const capacitacionesObligatorias = [
    { nombre: 'H2S (Sulfuro de Hidrógeno)', duracion: '8 horas', validez: '1 año', requerido: 'Campo' },
    { nombre: 'Trabajo en Altura', duracion: '16 horas', validez: '1 año', requerido: 'Campo' },
    { nombre: 'Espacios Confinados', duracion: '16 horas', validez: '1 año', requerido: 'Campo' },
    { nombre: 'Primeros Auxilios', duracion: '8 horas', validez: '2 años', requerido: 'Todos' },
    { nombre: 'Lucha contra Incendios', duracion: '8 horas', validez: '1 año', requerido: 'Campo' },
    { nombre: 'Manejo Defensivo', duracion: '4 horas', validez: '1 año', requerido: 'Conductores' },
  ];

  const empleadosCapacitaciones = [
    {
      id: 1,
      empleado: 'Juan Rodríguez',
      legajo: 'PL-001',
      area: 'Perforación',
      h2s: { estado: 'Vigente', vence: '15/06/2026' },
      altura: { estado: 'Por vencer', vence: '10/02/2026' },
      espacios: { estado: 'Vigente', vence: '20/08/2026' },
      primeros: { estado: 'Vigente', vence: '01/12/2027' },
    },
    {
      id: 2,
      empleado: 'Pedro García',
      legajo: 'PL-002',
      area: 'Workover',
      h2s: { estado: 'Por vencer', vence: '05/02/2026' },
      altura: { estado: 'Vigente', vence: '18/09/2026' },
      espacios: { estado: 'Vencido', vence: '10/01/2026' },
      primeros: { estado: 'Vigente', vence: '15/03/2027' },
    },
    {
      id: 3,
      empleado: 'Carlos Mendoza',
      legajo: 'PL-004',
      area: 'Seguridad',
      h2s: { estado: 'Vigente', vence: '22/07/2026' },
      altura: { estado: 'Vigente', vence: '22/07/2026' },
      espacios: { estado: 'Vigente', vence: '22/07/2026' },
      primeros: { estado: 'Vigente', vence: '22/07/2027' },
    },
    {
      id: 4,
      empleado: 'Ana Martínez',
      legajo: 'PL-005',
      area: 'Producción',
      h2s: { estado: 'Vencido', vence: '28/12/2025' },
      altura: { estado: 'Vigente', vence: '30/04/2026' },
      espacios: { estado: 'Por vencer', vence: '12/02/2026' },
      primeros: { estado: 'Vigente', vence: '10/10/2027' },
    },
  ];

  const getEstadoChip = (estado) => {
    switch (estado) {
      case 'Vigente':
        return <Chip icon={<CheckIcon />} label="Vigente" size="small" color="success" />;
      case 'Por vencer':
        return <Chip icon={<WarningIcon />} label="Por vencer" size="small" color="warning" />;
      case 'Vencido':
        return <Chip icon={<BlockIcon />} label="VENCIDO" size="small" color="error" />;
      default:
        return <Chip label="N/A" size="small" />;
    }
  };

  const resumenStats = [
    { label: 'Capacitaciones Vigentes', value: '89%', icon: <CheckIcon />, color: theme.palette.success.main },
    { label: 'Por Vencer (30 días)', value: 12, icon: <WarningIcon />, color: theme.palette.warning.main },
    { label: 'Vencidas', value: 8, icon: <BlockIcon />, color: theme.palette.error.main },
    { label: 'Programadas', value: 15, icon: <ScheduleIcon />, color: theme.palette.info.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Capacitaciones
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Control de capacitaciones obligatorias del personal
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />}>
            Programar Capacitación
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
            Registrar Capacitación
          </Button>
        </Box>
      </Box>

      {/* Alerta Importante */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>⚠️ Importante:</strong> El personal con capacitaciones vencidas (H2S, Altura, Espacios Confinados) 
          no puede ser asignado a trabajos de campo según normativa de seguridad industrial.
        </Typography>
      </Alert>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Capacitaciones Obligatorias */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Capacitaciones Obligatorias
          </Typography>
          <Grid container spacing={2}>
            {capacitacionesObligatorias.map((cap, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SchoolIcon color="primary" fontSize="small" />
                    <Typography variant="body2" fontWeight={600}>
                      {cap.nombre}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Duración: {cap.duracion} | Validez: {cap.validez}
                  </Typography>
                  <Chip label={`Requerido: ${cap.requerido}`} size="small" sx={{ mt: 1 }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Matriz de Capacitaciones */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Matriz de Capacitaciones por Empleado
          </Typography>

          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
            <Tab label="Todos" />
            <Tab label="Por Vencer" />
            <Tab label="Vencidos" />
          </Tabs>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Empleado</TableCell>
                  <TableCell>Área</TableCell>
                  <TableCell align="center">H2S</TableCell>
                  <TableCell align="center">Altura</TableCell>
                  <TableCell align="center">Esp. Confinados</TableCell>
                  <TableCell align="center">Primeros Aux.</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {empleadosCapacitaciones.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: '0.75rem' }}>
                          {row.empleado.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{row.empleado}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.legajo}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{row.area}</TableCell>
                    <TableCell align="center">
                      {getEstadoChip(row.h2s.estado)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.h2s.vence}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getEstadoChip(row.altura.estado)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.altura.vence}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getEstadoChip(row.espacios.estado)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.espacios.vence}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getEstadoChip(row.primeros.estado)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {row.primeros.vence}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined">
                        Actualizar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog Registrar Capacitación */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Capacitación</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Empleado</InputLabel>
                <Select label="Empleado">
                  <MenuItem value="1">Juan Rodríguez - PL-001</MenuItem>
                  <MenuItem value="2">Pedro García - PL-002</MenuItem>
                  <MenuItem value="4">Carlos Mendoza - PL-004</MenuItem>
                  <MenuItem value="5">Ana Martínez - PL-005</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Capacitación</InputLabel>
                <Select label="Capacitación">
                  <MenuItem value="h2s">H2S (Sulfuro de Hidrógeno)</MenuItem>
                  <MenuItem value="altura">Trabajo en Altura</MenuItem>
                  <MenuItem value="espacios">Espacios Confinados</MenuItem>
                  <MenuItem value="primeros">Primeros Auxilios</MenuItem>
                  <MenuItem value="incendios">Lucha contra Incendios</MenuItem>
                  <MenuItem value="manejo">Manejo Defensivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha de Realización"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha de Vencimiento"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Institución / Capacitador" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Número de Certificado" />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                Adjuntar Certificado (PDF)
                <input type="file" hidden accept=".pdf" />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Capacitaciones;
